<script lang="ts" setup>
import countTo from 'vue-count-to'
import { computed, onUnmounted, ref, watch } from 'vue'
import type { MemberTopupSubscribeProduct, SubscribePageConfig } from '@/composables/payment'
import {
  AutoRenewalNotice,
  PaymentMethodSelector,
  createShowPaymentMethodSelector,
  useSubscription,
} from '@/composables/payment'
import { useAxonViewItem } from '@/composables/axon/useAxonViewItem'
import { useFbqAddToCart } from '@/composables/fbq/useFbqAddToCart'
import { getDailyPrice, getSavePercent, getUnitPriceValue, shouldShowDailyPrice, shouldShowPreferentialTotal } from '@/composables/payment/utils/subscribeDailyPrice'
import { VIP_TYPE } from '@/constants'
import { t } from '@/i18n/utils'

const props = defineProps<{
  config: SubscribePageConfig
}>()

// 使用订阅 composable（支持多支付方式）
const {
  subscribeProducts,
  onSubscribeProducts,
  isZero,
  currentPaymentMethod,
  availablePaymentMethods,
  selectPaymentMethod,
} = useSubscription('store')

// 是否显示支付方式选择器
const showPaymentMethodSelector = createShowPaymentMethodSelector(availablePaymentMethods)

// Axon view_item 上报（商品加载 + 支付方式切换均触发，Set 内部去重）
const { reportViewItem } = useAxonViewItem()
watch(subscribeProducts, (val) => {
  if (val.length)
    reportViewItem(val, currentPaymentMethod.value, 'Membership', p => p.preferentialPrice ?? p.price)
})

const { reportAddToCart } = useFbqAddToCart()
watch(subscribeProducts, (val) => {
  for (const p of val)
    reportAddToCart(p.productId, 'Membership', p.preferentialPrice ?? p.price ?? 0)
})

const active = ref(0)
const discountModel = ref(false)
const discountCountdownSeconds = ref(15 * 60)
const discountCountdownTimer = ref<ReturnType<typeof setInterval> | null>(null)

const discountCountdown = computed(() => {
  const minutes = Math.floor(discountCountdownSeconds.value / 60)
  const seconds = discountCountdownSeconds.value % 60
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
})

watch(discountModel, (visible) => {
  if (discountCountdownTimer.value) {
    clearInterval(discountCountdownTimer.value)
    discountCountdownTimer.value = null
  }

  if (!visible)
    return

  discountCountdownSeconds.value = 15 * 60
  discountCountdownTimer.value = setInterval(() => {
    if (discountCountdownSeconds.value > 1) {
      discountCountdownSeconds.value -= 1
      return
    }

    discountCountdownSeconds.value = 0
    clearInterval(discountCountdownTimer.value!)
    discountCountdownTimer.value = null
  }, 1000)
})

const timer = ref<ReturnType<typeof setTimeout> | null>(null)
const currentDiscount: any = ref(null)
const discountNumber = ref(0)
const getDiscountPercentage = function () {
  if (subscribeProducts.value && subscribeProducts.value.length > 0) {
    let maxDiscount = 0
    let current = null
    subscribeProducts.value.forEach((item: MemberTopupSubscribeProduct) => {
      let discount = 0
      let shouldCalculate = false

      // 畅读会员：只有在 preferentialPrice 存在时才计算折扣(包括为0的情况)
      if (item.subscribeType === VIP_TYPE.ALL && (item.preferentialPrice !== undefined && item.preferentialPrice !== null)) {
        discount = (1 - (Number(item.preferentialPrice) / Number(item.price))) * 100
        shouldCalculate = true
      }

      // 折扣会员：有 discountOriginalPrice 就计算折扣
      if (item.subscribeType === VIP_TYPE.DISCOUNT && item.discountOriginalPrice) {
        discount = (1 - (Number(item.price) / Number(item.discountOriginalPrice))) * 100
        shouldCalculate = true
      }

      // 只有符合条件的会员才参与比较
      if (shouldCalculate && discount >= maxDiscount) {
        current = item
        maxDiscount = Math.round(discount)
      }
    })

    // 只要找到符合条件的会员就返回，不管折扣是多少(包括0)
    if (current)
      return [current, maxDiscount]
  }

  return false
}

function initDiscountModel() {
  const showDiscount = window.localStorage.getItem('showDiscount')
  if (showDiscount)
    return

  timer.value = setTimeout(() => {
    const discountResult = getDiscountPercentage()
    discountNumber.value = discountResult ? (discountResult[1] ?? 0) : 0
    currentDiscount.value = discountResult ? discountResult[0] : null

    if (!Array.isArray(discountResult)) {
      clearTimeout(timer.value!)
      return
    }

    discountModel.value = true
    window.localStorage.setItem('showDiscount', '1')
  }, 6000)
}
initDiscountModel()

function buyNow() {
  onSubscribeProducts(currentDiscount.value)
}

onUnmounted(() => {
  if (timer.value)
    clearTimeout(timer.value)
  if (discountCountdownTimer.value)
    clearInterval(discountCountdownTimer.value)
})

const vipIconVisible = props.config.resources.showVipIcon
</script>

<template>
  <div class="subscribe-cover">
    <BasicNavbar class="color-n-text-2">
      <div class="novelyn-nav-title">
        {{ config.texts.membership }}
      </div>
    </BasicNavbar>

    <div class="novelyn-vip-head">
      <div class="novelyn-vip-crown">
        <img src="/images/novelyn/crown.svg" alt="VIP">
      </div>
      <div class="novelyn-vip-title">
        Novelyn <i>VIP</i>
      </div>
      <div class="novelyn-vip-desc">
        {{ config.texts.membershipDesc }}
      </div>
    </div>

    <div class="member-wrap">
      <template v-for="(item, index) in subscribeProducts">
        <!-- 折扣会员：白色金币卡 -->
        <div
          v-if="item.isShow && item.subscribeType === VIP_TYPE.DISCOUNT"
          :key="`discount-${index}`"
          class="novelyn-sub-card"
          :class="{ 'is-active': active === index }"
          @click="() => { active = index; onSubscribeProducts(item); }"
        >
          <div v-if="item.cornerTip === 1 && item.marketingCopy" class="novelyn-corner-badge">
            {{ item.marketingCopy }}
          </div>
          <div class="sc-head">
            <img v-if="vipIconVisible" class="sc-coin" src="/images/novelyn/coin.svg" alt="coins">
            <span class="sc-title">{{ item.discountCoins }} {{ t('Coins') }}</span>
            <span class="sc-cycle">/ {{ item.cycleStr }}</span>
          </div>
          <div class="sc-price-row">
            <span class="sc-strike">$ {{ item.discountOriginalPrice }}</span>
            <span class="sc-big-price">
              <span class="price-counter">
                <span aria-hidden="true" class="invisible whitespace-nowrap">$ {{ Number(item.discountOriginalPrice).toFixed(2) }}</span>
                <span class="price-counter__value">$ <countTo
                  :start-val="Number(item.discountOriginalPrice)"
                  :end-val="Number(item.preferentialPrice || item.price)"
                  :duration="3000"
                  :decimals="2"
                /></span>
              </span>
            </span>
          </div>
          <div class="sc-check">
            <span class="sc-check-ico">✔</span>{{ config.texts.payTemplateGetCoins?.(item.discountOnceCoins) }}
          </div>
          <div class="sc-check">
            <span class="sc-check-ico">✔</span>{{ config.texts.payTemplateEarnCoins?.(item.discountBonusCoins, item.discountDailyCoins) }}
          </div>
        </div>

        <!-- 畅读会员：深色 VIP 卡 -->
        <div
          v-else-if="item.isShow"
          :key="`all-${index}`"
          class="novelyn-sub-card is-dark"
          :class="{ 'is-active': active === index }"
          @click="() => { active = index; onSubscribeProducts(item); }"
        >
          <div v-if="item.cornerTip === 1 && item.marketingCopy" class="novelyn-corner-badge is-gold">
            {{ item.marketingCopy }}
          </div>
          <div v-else-if="shouldShowDailyPrice(item, isZero)" class="novelyn-corner-badge is-gold">
            {{ t('SavePercent', { percent: getSavePercent(item) }) }}
          </div>

          <div class="sc-head">
            <span class="sc-title is-gold-text">{{ item.cycleStr }}</span>
          </div>

          <!-- 免费 -->
          <template v-if="isZero(item.preferentialPrice)">
            <div class="sc-price-row">
              <span class="sc-big-price is-gold-text">{{ config.texts.free }}</span>
            </div>
            <div class="sc-sub">
              <span class="sc-strike-light">$ {{ item.price }}</span>
            </div>
          </template>

          <!-- 无优惠价格：有每日单价时突出单价，总价置于下方 -->
          <template v-else-if="!item.preferentialPrice">
            <div class="sc-price-row">
              <span class="sc-big-price is-gold-text">$ {{ getUnitPriceValue(item) || item.price }}</span>
              <span v-if="getUnitPriceValue(item)" class="sc-unit"> {{ t('PerDay') }}</span>
            </div>
            <div v-if="getUnitPriceValue(item)" class="sc-sub">
              $ {{ item.price }}
            </div>
          </template>

          <!-- 有优惠价格 -->
          <template v-else>
            <div class="sc-price-row">
              <span class="sc-big-price is-gold-text">
                <template v-if="shouldShowDailyPrice(item, isZero)">$ {{ getDailyPrice(item) }}</template>
                <span v-else class="price-counter">
                  <span aria-hidden="true" class="invisible whitespace-nowrap">$ {{ Number(item.price).toFixed(2) }}</span>
                  <span class="price-counter__value">$ <countTo
                    :start-val="Number(item.price)"
                    :end-val="Number(item.preferentialPrice)"
                    :duration="3000"
                    :decimals="2"
                  /></span>
                </span>
              </span>
              <span v-if="shouldShowDailyPrice(item, isZero)" class="sc-unit"> {{ t('PerDay') }}</span>
            </div>
            <div class="sc-sub">
              <span class="sc-strike-light">$ {{ item.price }}</span>
              <span v-if="shouldShowPreferentialTotal(item, isZero)" class="sc-total">$ {{ item.preferentialPrice }}</span>
            </div>
          </template>

          <!-- 畅读会员权益文案:自然用户覆写为两行价格说明(参考折扣会员档位底部文案),广告用户展示接口文案 -->
          <div v-if="item.subscribeText" class="sc-benefit">
            <BasicFitLines v-if="item.subscribeTextOverridden" :text="item.subscribeText" />
            <div v-else class="sc-benefit-text">
              {{ item.subscribeText }}
            </div>
          </div>
        </div>
      </template>

      <!-- 编号条款 -->
      <div class="novelyn-sub-terms">
        <p>{{ config.texts.tip1 }}</p>
        <p>{{ config.texts.tip2 }}</p>
        <!-- 自动续费提示(仅自然用户展示;订阅中心无充值商品,不含括号内容) -->
        <AutoRenewalNotice has-subscribe :has-topup="false" prefix="3. " />
      </div>
    </div>

    <!-- 支付方式选择器固定在底部 -->
    <div v-if="showPaymentMethodSelector" class="novelyn-pay-bar fixed bottom-0px left-0 right-0 z-50 flex justify-center">
      <PaymentMethodSelector
        variant="large"
        :dark="config.darkPaymentSelector"
        :model-value="currentPaymentMethod"
        :available-methods="availablePaymentMethods"
        @update:model-value="selectPaymentMethod"
      />
    </div>

    <BasicModal v-model="discountModel" :transparent="true" :closable="false" class-name="novelyn-discount-modal">
      <div class="novelyn-discount-popup">
        <div class="novelyn-discount-head">
          <button class="novelyn-discount-close" type="button" :aria-label="t('Close')" @click="discountModel = false">
            &times;
          </button>
          <div class="novelyn-discount-kicker">
            {{ t('LimitedOffer') }}
          </div>
          <div class="novelyn-discount-value">
            {{ t('PercentOff', { percent: discountNumber }) }}
          </div>
          <div class="novelyn-discount-subtitle">
            {{ t('MembershipDiscount') }}
          </div>
        </div>
        <div class="novelyn-discount-body">
          <div class="novelyn-discount-timer">
            <span aria-hidden="true">⏳</span> {{ t('OfferEndsIn') }} <b>{{ discountCountdown }}</b>
          </div>
          <button class="novelyn-discount-claim" type="button" @click="buyNow">
            {{ t('ClaimPercentOffNow', { percent: discountNumber }) }}
          </button>
        </div>
      </div>
    </BasicModal>
  </div>
</template>

<style lang="scss" scoped>
.subscribe-cover {
  min-height: 100dvh;
  width: 100%;
  padding-bottom: 96px;
  background: var(--novelyn-bg);
}

.novelyn-nav-title {
  position: absolute;
  inset: 0 58px;
  display: grid;
  place-items: center;
  color: var(--novelyn-ink);
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 17px;
  font-weight: 700;
  pointer-events: none;
}

/* 头部 */
.novelyn-vip-head {
  padding: 58px 24px 8px;
  text-align: center;
}

.novelyn-vip-crown {
  line-height: 1.4;

  img {
    display: inline-block;
    width: 26px;
    height: auto;
  }
}

.novelyn-vip-title {
  color: var(--novelyn-ink);
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 22px;
  font-weight: 700;

  i {
    color: var(--novelyn-rose);
  }
}

.novelyn-vip-desc {
  margin-top: 4px;
  font-size: 11px;
  font-weight: 700;
  color: var(--novelyn-ink-2);
}

.member-wrap {
  margin-top: 8px;
}

/* 订阅卡 */
.novelyn-sub-card {
  position: relative;
  margin: 0 16px 14px;
  padding: 18px;
  overflow: hidden;
  border: 1.5px solid var(--novelyn-line);
  border-radius: 22px;
  background: #fff;
  box-shadow: var(--novelyn-shadow-sm);

  &.is-active {
    border-color: var(--novelyn-rose);
  }

  &.is-dark {
    color: #fff;
    border: none;
    background: linear-gradient(120deg, #3A2A33, #5C3A4A);
    box-shadow: 0 14px 30px rgba(58, 42, 51, .3);

    &.is-active {
      box-shadow: 0 0 0 1.5px rgba(239, 206, 122, .65), 0 14px 30px rgba(58, 42, 51, .3);
    }
  }
}

/* 角标 */
.novelyn-corner-badge {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 2;
  padding: 5px 12px;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: .5px;
  color: #fff;
  border-radius: 0 0 0 14px;
  background: var(--novelyn-gradient);
  box-shadow: -2px 2px 8px rgba(224, 90, 126, .3);

  &.is-gold {
    color: #5C430A;
    background: var(--novelyn-gold-gradient);
    box-shadow: -2px 2px 8px rgba(201, 162, 39, .3);
  }
}

/* 卡片内容 */
.sc-head {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sc-coin {
  width: 18px;
  height: 18px;
}

.sc-title {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 19px;
  font-weight: 700;
  color: var(--novelyn-ink);

  &.is-gold-text {
    color: #EFCE7A;
  }
}

.sc-cycle {
  font-size: 11px;
  font-weight: 700;
  color: var(--novelyn-ink-2);
}

.sc-price-row {
  display: flex;
  align-items: baseline;
  margin: 8px 0 10px;

  .is-dark & {
    margin-bottom: 4px;
  }
}

.sc-strike {
  font-size: 12px;
  font-weight: 700;
  color: var(--novelyn-ink-3);
  text-decoration: line-through;
}

.sc-strike-light {
  font-size: 12px;
  font-weight: 700;
  color: rgba(255, 255, 255, .5);
  text-decoration: line-through;
}

.sc-big-price {
  margin-left: 6px;
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 28px;
  font-weight: 800;
  line-height: 1.1;
  color: var(--novelyn-rose-deep);

  &.is-gold-text {
    margin-left: 0;
    color: #EFCE7A;
  }
}

.sc-unit {
  font-size: 12px;
  font-weight: 700;
  opacity: .75;
}

.sc-sub {
  font-size: 12px;
  font-weight: 700;
  opacity: .85;

  .sc-total {
    margin-left: 8px;
  }
}

/* 权益行 */
.sc-check {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  font-size: 12px;
  font-weight: 800;
  color: var(--novelyn-ink);
}

.sc-check-ico {
  color: var(--novelyn-ok);
}

/* 畅读会员权益文案(参考折扣会员档位底部两行文案样式) */
.sc-benefit {
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, .14);
  font-size: 11px;
  font-weight: 700;
  line-height: 1.6;
  color: rgba(255, 255, 255, .72);

  :deep(.basic-fit-lines) {
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
    color: inherit;
  }
}

.sc-benefit-text {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

/* 编号条款 */
.novelyn-sub-terms {
  margin: 2px 20px 90px;
  font-size: 9.5px;
  font-weight: 600;
  line-height: 1.7;
  color: var(--novelyn-ink-3);

  p {
    margin-top: 4px;
  }
}

/* 底部支付方式栏 */
.novelyn-pay-bar {
  width: 100%;
  background: rgba(255, 255, 255, .96);
  border-top: 1px solid var(--novelyn-line);
  backdrop-filter: blur(10px);

  :deep(.payment-method-selector-large) {
    gap: 10px;
    margin: 0;
    padding: 12px 16px 20px;
    background: transparent;
  }

  :deep(.payment-method-btn-large) {
    flex: 1 1 0;
    width: auto;
    height: auto;
    padding: 13px 0;
    border: 1.5px solid var(--novelyn-line);
    border-radius: 16px;
    background: #fff;
    box-shadow: var(--novelyn-shadow-sm);
  }

  :deep(.payment-method-btn-large.active),
  :deep(.payment-method-btn-large.active.paypal) {
    border-color: var(--novelyn-rose);
    background: var(--novelyn-rose-faint);
    box-shadow: 0 8px 18px rgba(224, 90, 126, .2);
  }
}

/* 价格倒数动画：用不可见的原价占位撑出固定宽度，动画数字绝对定位叠加其上 */
.price-counter {
  position: relative;
  display: inline-block;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.price-counter__value {
  position: absolute;
  top: 0;
  left: 0;
  white-space: nowrap;
}

.novelyn-discount-popup {
  width: 300px;
  overflow: hidden;
  color: var(--novelyn-ink);
  text-align: center;
  border-radius: 24px;
  background: #fff;
  box-shadow: 0 20px 50px rgba(64, 20, 40, .3);
}

.novelyn-discount-head {
  position: relative;
  padding: 30px 20px 24px;
  color: #fff;
  background: var(--novelyn-gradient);
}

.novelyn-discount-close {
  position: absolute;
  top: 8px;
  right: 12px;
  display: grid;
  width: 24px;
  height: 24px;
  padding: 0;
  color: inherit;
  font-size: 22px;
  line-height: 1;
  border: 0;
  background: transparent;
  opacity: .8;
  place-items: center;
}

.novelyn-discount-kicker {
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 3px;
  opacity: .85;
}

.novelyn-discount-value {
  margin: 6px 0;
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 40px;
  font-weight: 700;
  line-height: 1;
}

.novelyn-discount-subtitle {
  font-size: 12px;
  font-weight: 700;
  opacity: .9;
}

.novelyn-discount-body {
  padding: 18px 22px 22px;
}

.novelyn-discount-timer {
  margin-bottom: 14px;
  color: var(--novelyn-ink-2);
  font-size: 12px;
  font-weight: 700;

  b {
    color: var(--novelyn-rose-deep);
  }
}

.novelyn-discount-claim {
  width: 100%;
  padding: 13px 20px;
  color: #fff;
  font-size: 14px;
  font-weight: 800;
  border: 0;
  border-radius: 999px;
  background: var(--novelyn-gradient);
  box-shadow: 0 8px 18px rgba(224, 90, 126, .35);
}
</style>

<style lang="scss">
.novelyn-discount-modal {
  > .bg-n-modal-mask {
    backdrop-filter: blur(2px);
  }
}
</style>
